import { SweetspotError } from '@/helpers/error'

const DEFAULT_REQUEST_ERROR_MESSAGE = 'Request failed.'
const GENERIC_STATUS_TEXT_MESSAGES = new Set([
  'Bad Request',
  'Unauthorized',
  'Forbidden',
  'Not Found',
  'Conflict',
  'Unprocessable Entity',
  'Internal Server Error',
])

interface PromptValidationError {
  msg?: string
  loc?: string[]
}

interface PromptErrorResponse {
  detail?: string | PromptValidationError[]
}

export const getPromptRequestErrorMessage = async (
  res: Response,
): Promise<string> => {
  try {
    const data = (await res.json()) as PromptErrorResponse

    if (typeof data.detail === 'string' && data.detail.trim()) {
      return data.detail
    }

    if (Array.isArray(data.detail)) {
      const firstError = data.detail[0]
      if (firstError?.msg) {
        return firstError.msg
      }
    }
  } catch {
    // Fall back to the HTTP status text when the response is not JSON.
  }

  return res.statusText || DEFAULT_REQUEST_ERROR_MESSAGE
}

/**
 * One authenticated prompts API call. A failure carries the server's own
 * message and status; a success returns the parsed body, or undefined when
 * the response has none.
 */
export const promptRequest = async <T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  accessToken: string | undefined | null,
  body?: unknown,
): Promise<T> => {
  if (accessToken === null || accessToken === undefined) {
    throw new SweetspotError({
      name: 'NO_ACCESS_TOKEN_PROVIDED_ERROR',
      message: 'No access token provided.',
    })
  }
  const res = await fetch(url, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: await getPromptRequestErrorMessage(res),
      cause: { status: res.status },
    })
  }
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * The trailing window's days as the server buckets usage (UTC, YYYY-MM-DD),
 * oldest first and ending on today's UTC date.
 */
export const usageWindowDays = (windowDays: number, now = new Date()) => {
  const today = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  )
  return Array.from({ length: windowDays }, (_, index) =>
    new Date(today - (windowDays - 1 - index) * DAY_MS)
      .toISOString()
      .slice(0, 10),
  )
}

export const getPromptToastErrorMessage = (
  error: Error | SweetspotError,
): string | undefined => {
  const statusCode =
    error instanceof SweetspotError
      ? error.cause?.status
      : ((error as { cause?: { status?: number }; status?: number }).cause
          ?.status ?? (error as { status?: number }).status)

  if (!error.message || error.message === DEFAULT_REQUEST_ERROR_MESSAGE) {
    return undefined
  }

  if (statusCode && GENERIC_STATUS_TEXT_MESSAGES.has(error.message)) {
    return undefined
  }

  return error.message
}
